import { useState, useEffect } from "react";
import DashboardLayout from "../../../Layouts/Dashboard/dashboard";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import TvIcon from "@mui/icons-material/Tv";

import {
  Stack,
  Typography,
  Button,
  Grid2 as Grid,
  MenuItem,
  Select,
  Box,
  styled,
  SxProps,
  SelectChangeEvent,
} from "@mui/material";
import { useParams } from "react-router";

import { GridColDef, GridValidRowModel } from "@mui/x-data-grid";

import DataTable, { CellSize } from "../../../Components/Table/table";
import TableCellOrderStatus from "../../../Components/Table/TableCellOrderStatus/table-cell-order-status";
import OrderCard from "../../../Components/Cards/OrderCard/orderCard";
import OrderDetailsSubbar from "../../../Components/Subbars/OrderDetailsSubbar";
import OrderItemPanel from "../../../Components/Panels/OrderItemPanel/order-item-panel";
import ToggleLayout from "../../../Components/Misc/ToggleLayout";
import SelectModeSubbarOpener from "../../../Components/Banners/SelectModeBanner/opener";
import { handleSelectClickOrderItem, isItemSelectedNumber } from "../../../Utils/Helpers/selectMode";
import OrderSelectModeSubbar from "../../../Components/Banners/OrderSelectModeBanner/banner-order";
import BoxPaper from "../../../Components/Misc/BoxPaper";
import MetadataListTextItem from "../../../Components/Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import { orderService, thumbnailService } from "../../../Core/Api/services";
import { GetOrderResponse } from "../../../Core/Api/Types/get-order-response";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";
import { Distinct, GetCurrencyChar, GetOrderProductReadableType, UserHasAcl } from "../../../Utils/helpers";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import TableSkeleton from "../../../Components/Table/TableSkeleton";
import moment from "moment";
import { CurrencyTypes } from "../../../Core/Api/Types/currency-types";
import OrderProductIcon from "../../../Components/Misc/OrderProductIcon/order-product-icon";
import SinglePageHeader, { SinglePageHeaderGroupActions } from "../../../Components/Misc/SinglePageHeader";
import { ActivityKind } from "../../../Core/Api/Types/activity-kind";
import ResourceNotification from "../../../Components/Notification/resource-notification";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";
import { t } from "i18next"; // TODO: Translation -> check this

const getColumns = (): GridColDef[] => {
  const columns: GridColDef[] = [
    { field: "id", headerName: "Id", flex: 50 },
    {
      field: "config",
      headerName: t("table:headerName.configType"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data: any) => (
        <Stack direction="row" spacing={1} alignItems="center" overflow="hidden">
          <OrderProductIcon type={data.value.type} color="info" />
          <Box overflow="hidden">
            <Typography fontWeight={500} sx={(theme) => ({ ...theme.helpers(theme).ellipsis })}>
              {GetOrderProductReadableType(data.value.type)}
            </Typography>
            {/*<Stack direction="row" sx={{ svg: { opacity: 0.5 } }} fontSize={10}>
                  <ConfigContentRecaps {...data.value.config} fontSize={10} />
                </Stack>*/}
          </Box>
        </Stack>
      ),
    },
    {
      field: "content",
      headerName: t("common:content"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data: any) => (
        <Stack>
          <Typography fontWeight={500}>{data.value.layer}</Typography>
          <Typography color="text.secondary">{data.value.titleName}</Typography>
        </Stack>
      ),
    },
    { field: "price", headerName: t("common:price"), flex: CellSize.xsmall, minWidth: CellSize.xsmall },
    {
      field: "status",
      headerName: t("common:status"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: any) => <TableCellOrderStatus data={data} />,
    },
  ];

  return columns;
};

function OrderSingleAdvanced() {
  const { t } = useTranslation();
  const [configPanelOpened, setConfigPanelOpened] = useState(false);
  const [selectedOrderItemId, setSelectedOrderItemId] = useState<number | null>(null);
  const [layout, setLayout] = useState<"grid" | "list">("grid");
  const [selectedConfigs, setSelectedConfigs] = useState<DbOrderItemInfos[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState<GetOrderResponse>();
  const [rows, setDataGrid] = useState<GridValidRowModel>([]);
  const [filterProduct, setFilterProduct] = useState("");
  const [filterContent, setFilterContent] = useState("");
  const [activitiesShowComments, setActivityShowComments] = useState(false);

  const onLayoutChange = (e: React.MouseEvent<HTMLElement>, value: "grid" | "list") => {
    setLayout(value);
  };

  const { orderId } = useParams();
  const orderIdInteger = parseInt(orderId!);

  // Map items data to order data level
  const mapDataToGrid = (items: DbOrderItemInfos[]): GridValidRowModel => {
    let output: GridValidRowModel = [];

    items.forEach((item) => {
      output.push({
        id: item.id,
        item: item,
        config: { type: item.type!, config: { setting: item.summarizedInfos } },
        content: {
          layer: item.objectLayer,
          titleName: item.objectName,
        },
        price: item.price !== null ? item.price?.price + " " + GetCurrencyChar(item.price?.currency!) : 0.0,
        status: item.status,
      });
    });

    return output;
  };

  // Fetching data
  useEffect(() => {
    orderService.GetOrderById(orderIdInteger).then(function (response) {
      setOrderData(response);
      setDataGrid(mapDataToGrid(response.order?.items!));
      setLoading(false);
    });
  }, [orderIdInteger]);

  const getDueDateDays = () => {
    if (orderData?.order?.dueDate === null) return t("common:notDefined");

    let dateNow = moment();
    let dateDue = moment(orderData?.order?.dueDate);

    return t("common:countDays", { count: dateDue.diff(dateNow, "days") });
  };

  const hidePanelConfig = () => {
    setSelectedOrderItemId(null);
    setConfigPanelOpened(false);
  };

  const openConfigPanel = (itemId: number) => {
    setSelectedOrderItemId(itemId);
    setConfigPanelOpened(true);
  };

  const handleFilterProduct = (event: SelectChangeEvent) => {
    const p = event.target.value as string;
    setFilterProduct(p);
  };

  const handleFilterContent = (event: SelectChangeEvent) => {
    const p = event.target.value as string;
    setFilterContent(p);
  };

  const OrderFilters: React.FC<{ sx?: SxProps }> = (props) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={props.sx}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Select
          displayEmpty
          IconComponent={ExpandMoreRoundedIcon}
          startAdornment={<MovieCreationOutlinedIcon />}
          inputProps={{ "aria-label": "Without label" }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          value={filterContent}
          onChange={handleFilterContent}
        >
          <MenuItem value="">{t("common:allContents")}</MenuItem>
          <MenuItem value="Asset">{t("common:assetUpperCase")}</MenuItem>
          <MenuItem value="Title">{t("common:title")}</MenuItem>
          <MenuItem value="Clip">{t("common:upperCaseClip")}</MenuItem>
        </Select>
        <Select
          displayEmpty
          IconComponent={ExpandMoreRoundedIcon}
          startAdornment={<TvIcon />}
          inputProps={{ "aria-label": "Without label" }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          value={filterProduct}
          onChange={handleFilterProduct}
        >
          <MenuItem value="">{t("menu:item.allProducts")}</MenuItem>
          <MenuItem value="FaspexSend">{t("common:delivery")}</MenuItem>
          <MenuItem value="TranscodeByProfile">{t("menu:item.selfBroadcastedFile")}</MenuItem>
          <MenuItem value="TranscodeBundleByProfile">{t("menu:item.bundleMakerPackage")}</MenuItem>
          <MenuItem value="TranscodeGenericSingleFile">{t("menu:item.transcodeFromAsset")}</MenuItem>
          <MenuItem value="TranscodeGeneric">{t("menu:item.transcodeFromTitle")}</MenuItem>
          <MenuItem value="TranscodeDownscaleAsMezz">{t("menu:item.transcodeAsMezz")}</MenuItem>
          <MenuItem value="TranscodeSequence">{t("menu:item.transcodeSequence")}</MenuItem>
          <MenuItem value="TranscodeImf">{t("menu:item.transcodeIMF")}</MenuItem>
          <MenuItem value="TranscodeDcp">{t("menu:item.transcodeDCP")}</MenuItem>
          <MenuItem value="SubtitleConverter">{t("menu:item.subtitleConversion")}</MenuItem>
          <MenuItem value="ImageConverter">{t("menu:item.imageConversion")}</MenuItem>
          <MenuItem value="DcpReferenceFileSimple">{t("menu:item.dcpRefFileSimple")}</MenuItem>
          <MenuItem value="DcpReferenceFileStitched">{t("menu:item.dcpRefFileStitched")}</MenuItem>
          <MenuItem value="InternalProxyTag">{t("menu:item.proxyTagScreening")}</MenuItem>
          <MenuItem value="AntipiracyYoutube">{t("menu:item.youtubeAntipiracy")}</MenuItem>
          <MenuItem value="YoutubeChannelDelivery">{t("menu:item.youtubeChannelDelivery")}</MenuItem>
          <MenuItem value="QualityCheck">{t("menu:item.qualityCheckLowerCase")}</MenuItem>
          <MenuItem value="Qualify">{t("common:qualify")}</MenuItem>
          <MenuItem value="ManualTasks">{t("common:manualTasks")}</MenuItem>
          <MenuItem value="SubtitleScribeProject">{t("menu:item.subtitlingProject")}</MenuItem>
        </Select>
      </Stack>

      <ToggleLayout value={layout} onChange={onLayoutChange} />
    </Stack>
  );

  const handleSelectModeSubbarCloseClick = () => {
    setSelectedConfigs([]);
  };

  return (
    <DashboardLayout
      sxContent={{ px: 2 }}
      Subbar={
        <OrderDetailsSubbar
          start={<OrderFilters />}
          end={
            <>
              <Button variant="icon">
                <EditOutlinedIcon />
              </Button>
              <Button variant="icon" sx={{ ml: 1 }}>
                <ShareOutlinedIcon />
              </Button>
              <Button variant="contained" color="primary" startIcon={<FileDownloadOutlinedIcon />} sx={{ ml: 3 }}>
                {t("common:export")}
              </Button>
            </>
          }
        />
      }
      Banner={
        <OrderSelectModeSubbar selectedItems={selectedConfigs} handleCloseClick={handleSelectModeSubbarCloseClick} />
      }
    >
      <SelectModeSubbarOpener selectedObjects={selectedConfigs.map((x) => x.id)} />
      <SinglePageHeader
        mb={3}
        title={orderData?.order?.reference ?? GlobalConst.LOADING_TEXT}
        subtitle={t("page:singlePageHeader.subtitle.orderDetail")}
        backButton={{
          label: t("button:backToOrders"),
          to: "",
          toPrevious: true,
        }}
      >
        {/* For now, do not show menu buttons */}
        <SinglePageHeaderGroupActions sx={{ display: "none" }}>
          <Button startIcon={<EditOutlinedIcon />}>{t("common:editUpperCase")}</Button>
          <Button startIcon={<ShareOutlinedIcon />} sx={{ ml: 1 }}>
            {t("common:share")}
          </Button>
          <Button variant="contained" color="primary" startIcon={<FileDownloadOutlinedIcon />} sx={{ ml: 3 }}>
            {t("common:export")}
          </Button>
        </SinglePageHeaderGroupActions>
      </SinglePageHeader>
      <Grid container spacing={3}>
        <Grid size={8}>
          <OrderFilters sx={{ mb: 3 }} />

          {layout === "grid" ? (
            isLoading ? (
              <TableSkeleton />
            ) : (
              <CardGrid>
                {orderData?.order?.items
                  .filter(
                    (x) =>
                      (filterProduct === "" || x.type === filterProduct) &&
                      (filterContent === "" || x.objectLayer == filterContent)
                  )
                  .map((item: DbOrderItemInfos, index: number) => (
                    <OrderCard
                      index={item.id}
                      item={item}
                      key={item.id}
                      config={{
                        title: GetOrderProductReadableType(item.type!),
                        price:
                          item.price !== null ? item.price.price + " " + GetCurrencyChar(item.price.currency!) : "",
                        type: item.type!,
                        content: { setting: item.summarizedInfos! },
                        status: item.status!,
                        activities: item.activities?.length ?? 0,
                      }}
                      title={item.titleName!}
                      subtitle={item.objectLayer === ObjectLayer.Asset ? item.objectName! : item.objectLayer!}
                      thumbnail={thumbnailService.GetObjectPosterUrl(item.relatedTitleId)}
                      onClick={() => {
                        openConfigPanel(item.id);
                      }}
                      onSelectClick={handleSelectClickOrderItem(selectedConfigs, setSelectedConfigs)}
                      selected={isItemSelectedNumber(
                        item.id,
                        selectedConfigs.map((x) => x.id)
                      )}
                      selectedOnClick={selectedConfigs.length > 0 ? true : false}
                    />
                  ))}
              </CardGrid>
            )
          ) : (
            <Stack px={2}>
              <DataTable
                noControls
                selectedRows={selectedConfigs?.map((x) => x.id)}
                setSelectedRows={(e) =>
                  setSelectedConfigs(rows.map((x) => x.item).filter((x) => (e as number[]).includes(x.id)))
                }
                rows={rows.filter(
                  (x) =>
                    (filterProduct === "" || x.type === filterProduct) &&
                    (filterContent === "" || x.content.layer == filterContent)
                )}
                columns={getColumns()}
                getRowHeight={() => "auto"}
                checkboxSelection
                disableRowSelectionOnClick
                onRowClick={(event) => openConfigPanel(event.row.id)}
              />
            </Stack>
          )}
        </Grid>
        <Grid size={4}>
          <BoxPaper title={t("page:boxPaper.title.orderSummary")} mb={2}>
            <Stack spacing={1}>
              <MetadataListTextItem title={t("common:creationDateLowerCase")}>
                {moment(orderData?.order?.creationTime!).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
              </MetadataListTextItem>
              <MetadataListTextItem title={t("common:titles")}>
                {orderData?.order?.items.map((x) => x.relatedTitleId).filter(Distinct).length}
              </MetadataListTextItem>
              <MetadataListTextItem title={t("common:configurations")}>
                {orderData?.order?.items.length}
              </MetadataListTextItem>
              <MetadataListTextItem title={t("common:dueDate")} color="warning.main">
                <WarningAmberRoundedIcon fontSize="small" sx={{ verticalAlign: "text-bottom", mr: 0.5 }} />
                {getDueDateDays()}
              </MetadataListTextItem>
              <MetadataListTextItem title={t("page:body.totalPrice")} color="primary.text">
                {orderData?.order?.totalPrices?.find((x) => x.currency === CurrencyTypes.EUR)?.price} €
              </MetadataListTextItem>
              <Stack>
                <Typography variant="h6" mt={2} color="text.secondary">
                  {t("page:body.additionalComment")}
                </Typography>
                <Typography color="text.tier">{orderData?.order?.comment}</Typography>
              </Stack>
            </Stack>
          </BoxPaper>

          <BoxPaper
            title={t("common:activities")}
            rightContent={
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddOutlinedIcon />}
                onClick={() => setActivityShowComments(true)}
                disabled={!UserHasAcl(AclTypes.aclActivityAdd)}
              >
                {t("modal:title.addComment")}
              </Button>
            }
          >
            <ResourceNotification
              resourceId={orderData?.order?.id}
              resourceKind={ActivityKind.Order}
              enableComments={activitiesShowComments}
            />
          </BoxPaper>
        </Grid>
      </Grid>
      {selectedOrderItemId !== null && (
        <OrderItemPanel open={configPanelOpened} onClose={hidePanelConfig} itemId={selectedOrderItemId} />
      )}
    </DashboardLayout>
  );
}

const CardGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gridGap: theme.spacing(2),
}));

export default OrderSingleAdvanced;
